import React, { useEffect, useLayoutEffect, useState, useRef, memo, useMemo } from 'react';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import {
  Box,
  Paper,
  Typography,
  IconButton,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Autocomplete,
} from '@mui/material';
// keyframes moved to raw CSS <style> tag to prevent animation restarts
import {
  Add as AddIcon,
  Settings as SettingsIcon,
  Delete as DeleteIcon,
  TrendingUp,
  TrendingDown,
  TrendingFlat,
} from '@mui/icons-material';
import {
  fetchTickerTapePreferences,
  saveTickerTapePreferences,
  fetchLivePrices,
  addTicker,
  removeTicker,
  updatePrices,
} from '../store/tickerTapeSlice';
import { fetchAllTickers } from '../store/tickersSlice';
import { getWsUrl } from '../config/api.config';
import useMarketSession from '../hooks/useMarketSession';
import { admitPriceUpdate, priceBasis, CLOSE_PRINT_GRACE_MS } from '../utils/session';

const WS_URL = getWsUrl();
// Same pause as the matrix socket (hooks/useWebSocket.js): a tab hidden this
// long drops its tape socket, and reconnects - with a fresh snapshot from the
// subscribe - when shown again. Hidden tabs' tapes were ~143 of the ~220 KB/min
// of production WebSocket egress (measured 2026-09-28), streaming to no one.
const HIDDEN_PAUSE_MS = 60 * 1000;

// Fixed widths for the numeric fields, in `ch` units against a tabular-numerals
// font. This is what makes the marquee seamless: prices are written straight
// into the DOM as they tick ($9.75 -> $10.42, +1.00 -> +10.25), and if the
// fields resized with their content, the measured copy width would stop
// matching --scroll-dist and the loop would visibly jump at every seam.
const PRICE_FIELD_CH = 8;   // "$1234.56"
const CHANGE_FIELD_CH = 17; // "-100.25 (-10.25%)"

const numericFieldSx = (ch) => ({
  fontSize: '0.65rem',
  fontVariantNumeric: 'tabular-nums',
  fontFeatureSettings: '"tnum"',
  display: 'inline-block',
  minWidth: `${ch}ch`,
  textAlign: 'right',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
});

// Ticker item — static layout, prices updated by parent via data attributes
const TickerItem = memo(({ ticker }) => {
  return (
    <Box
      data-ticker={ticker}
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        mx: 1,
        px: 1,
        py: 0.25,
        borderRadius: 0.5,
        backgroundColor: 'background.paper',
        border: '1px solid',
        borderColor: 'divider',
        flex: '0 0 auto',
      }}
    >
      <Typography sx={{ fontSize: '0.65rem', fontWeight: 'bold', mr: 0.5, whiteSpace: 'nowrap' }}>
        {ticker}
      </Typography>
      <Typography className="tape-price" sx={{ ...numericFieldSx(PRICE_FIELD_CH), mr: 0.5 }}>
        --
      </Typography>
      <Typography className="tape-change" sx={numericFieldSx(CHANGE_FIELD_CH)}>
        0.00 (0.00%)
      </Typography>
    </Box>
  );
});

// Scroll speed in CSS pixels per second, and the floor on a full loop.
const SCROLL_PX_PER_SEC = 60;
const MIN_DURATION_S = 15;

/**
 * Read the element's current horizontal translation, in px.
 * Used to preserve visual position when the animation is re-parameterised.
 */
const currentTranslateX = (el) => {
  const t = window.getComputedStyle(el).transform;
  if (!t || t === 'none') return 0;
  // matrix(a, b, c, d, tx, ty) | matrix3d(... , tx, ty, tz, 1)
  const nums = t.match(/matrix3d?\(([^)]+)\)/);
  if (!nums) return 0;
  const parts = nums[1].split(',').map(v => parseFloat(v.trim()));
  return t.startsWith('matrix3d') ? (parts[12] || 0) : (parts[4] || 0);
};

const TickerTape = () => {
  const dispatch = useDispatch();
  // Use separate selectors to minimize re-renders
  const tickers = useSelector((state) => state.tickerTape.tickers, shallowEqual);
  const rawPrices = useSelector((state) => state.tickerTape.prices);
  const allTickers = useSelector((state) => state.tickers.tickers, shallowEqual);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [newTicker, setNewTicker] = useState('');
  const contentRef = useRef(null);
  const singleCopyRef = useRef(null);  // Ref to measure single copy width
  const containerRef = useRef(null);  // Ref to measure container width
  const [animationDuration, setAnimationDuration] = useState(30);
  const [scrollDistance, setScrollDistance] = useState(0);
  const [numCopies, setNumCopies] = useState(2);

  // rawPrices used directly for DOM updates (no throttling needed — DOM writes are cheap)

  // WebSocket reference
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);

  // Fetch initial data - delay to not block main dashboard load
  useEffect(() => {
    const timer = setTimeout(() => {
      dispatch(fetchTickerTapePreferences());
    }, 500); // Delay ticker tape loading
    return () => clearTimeout(timer);
  }, [dispatch]);

  // WebSocket connection for live price updates
  useEffect(() => {
    if (tickers.length === 0) return;
    // Set by cleanup. close() fires onclose asynchronously, AFTER cleanup has
    // run, and onclose schedules a reconnect with this effect's (old) tickers —
    // so every tape edit used to leave one socket open for good.
    let disposed = false;
    // Set while the tab has been hidden for HIDDEN_PAUSE_MS; no reconnects then.
    let paused = false;
    let hideTimer = null;
    let initialTimer = null;

    const connectWebSocket = () => {
      if (disposed || paused) return;
      try {
        wsRef.current = new WebSocket(WS_URL);

        wsRef.current.onopen = () => {
          // Subscribe to price updates for our tickers
          wsRef.current.send(JSON.stringify({
            type: 'subscribe',
            tickers: tickers
          }));
        };

        wsRef.current.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data);
            if (data.type === 'priceUpdate' && data.data) {
              // Not a regular session: no live ticks (see admitPriceUpdate).
              const admitted = admitPriceUpdate(data.data);
              if (admitted && Object.keys(admitted).length > 0) dispatch(updatePrices(admitted));
            }
          } catch (e) {
            // Ignore parse errors
          }
        };

        wsRef.current.onclose = () => {
          // Reconnect after 5 seconds
          if (!disposed && !paused) reconnectTimeoutRef.current = setTimeout(connectWebSocket, 5000);
        };

        wsRef.current.onerror = () => {
          wsRef.current?.close();
        };
      } catch (error) {
        // Retry connection after 5 seconds
        if (!disposed && !paused) reconnectTimeoutRef.current = setTimeout(connectWebSocket, 5000);
      }
    };

    const closeWebSocket = () => {
      clearTimeout(initialTimer);
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
      if (wsRef.current) {
        wsRef.current.onclose = null;
        wsRef.current.onerror = null;
        wsRef.current.close();
        wsRef.current = null;
      }
    };

    // Pause while the tab is hidden; resume when it is shown again.
    const onVisibility = () => {
      if (document.hidden) {
        clearTimeout(hideTimer);
        hideTimer = setTimeout(() => {
          paused = true;
          closeWebSocket();
        }, HIDDEN_PAUSE_MS);
      } else {
        clearTimeout(hideTimer);
        hideTimer = null;
        if (paused) {
          paused = false;
          connectWebSocket();
        }
      }
    };
    document.addEventListener('visibilitychange', onVisibility);
    // A tab opened in the background gets no visibilitychange until it is shown.
    if (document.hidden) onVisibility();

    // Delay initial connection to prioritize dashboard data
    initialTimer = setTimeout(connectWebSocket, 1000);

    return () => {
      disposed = true;
      document.removeEventListener('visibilitychange', onVisibility);
      clearTimeout(hideTimer);
      closeWebSocket();
    };
  }, [dispatch, tickers]);

  // Off the regular session the socket's frames are not applied, so the tape's
  // prices come from the REST route, which answers by the same session rules
  // (previous close before 09:30, the close after 16:00) and has no cache that
  // could hand back a stale trade. Fetched when the tape's tickers are known
  // and again when the session turns over; when the regular session has just
  // ended it waits out the closing print so the last trade gives way to the close.
  const session = useMarketSession();
  const prevSessionRef = useRef(session);
  const tickersRef = useRef(tickers);
  tickersRef.current = tickers;
  // At a session change ask the server for a snapshot again (it answers a
  // subscribe with one): picks up a first trade that arrived a moment before
  // this clock reached 09:30 and so was not applied.
  useEffect(() => {
    const sock = wsRef.current;
    if (sock && sock.readyState === WebSocket.OPEN) {
      sock.send(JSON.stringify({ type: 'subscribe', tickers: tickersRef.current }));
    }
  }, [session]);
  useEffect(() => {
    const prev = prevSessionRef.current;
    prevSessionRef.current = session;
    if (session === 'regular' || tickers.length === 0) return undefined;
    const delay = prev === 'regular' ? CLOSE_PRINT_GRACE_MS + 5000 : 0;
    const timer = setTimeout(() => dispatch(fetchLivePrices(tickers)), delay);
    return () => clearTimeout(timer);
  }, [session, tickers, dispatch]);

  // The full ticker universe only feeds the add-ticker box, so fetch it when
  // that dialog opens rather than on every dashboard load.
  useEffect(() => {
    if (settingsOpen && (!allTickers || allTickers.length === 0)) {
      dispatch(fetchAllTickers());
    }
  }, [settingsOpen, allTickers, dispatch]);

  // Measure the rendered width of one copy and derive the scroll geometry.
  //
  // Re-measures whenever the ticker LIST changes (not just its length — the
  // slice seeds 5 defaults that the saved preferences replace, often with a
  // different 5 symbols), on container resize, and once web fonts have loaded,
  // since Roboto swapping in changes every width.
  useEffect(() => {
    if (tickers.length === 0) return undefined;

    let cancelled = false;
    let retryTimer = null;
    let retries = 0;

    const measure = () => {
      if (cancelled) return;
      if (!singleCopyRef.current || !containerRef.current) return;

      // getBoundingClientRect is sub-pixel accurate; offsetWidth rounds to an
      // integer, and that rounding error accumulates into seam drift.
      const copyWidth = singleCopyRef.current.getBoundingClientRect().width;
      const containerWidth = containerRef.current.getBoundingClientRect().width;

      if (copyWidth <= 0) {
        // Layout not ready yet (hidden tab, fonts still loading). Keep trying —
        // the old code gave up after one attempt, leaving the tape frozen.
        if (retries++ < 20) retryTimer = setTimeout(measure, 100);
        return;
      }

      // Enough copies to cover the viewport plus a full scroll distance, so the
      // seam can never expose a gap.
      const copiesNeeded = Math.max(2, Math.ceil(containerWidth / copyWidth) + 1);
      const duration = Math.max(MIN_DURATION_S, copyWidth / SCROLL_PX_PER_SEC);

      setNumCopies(prev => (prev === copiesNeeded ? prev : copiesNeeded));
      setScrollDistance(prev => (Math.abs(prev - copyWidth) < 0.5 ? prev : copyWidth));
      setAnimationDuration(prev => (Math.abs(prev - duration) < 0.01 ? prev : duration));
    };

    // Two frames so layout has settled before measuring.
    const raf = requestAnimationFrame(() => requestAnimationFrame(measure));

    // Fonts change metrics after they swap in.
    if (document.fonts?.ready) {
      document.fonts.ready.then(() => { if (!cancelled) measure(); }).catch(() => {});
    }

    // Container resize changes how many copies are needed.
    const ro = new ResizeObserver(measure);
    if (containerRef.current) ro.observe(containerRef.current);
    if (singleCopyRef.current) ro.observe(singleCopyRef.current);

    return () => {
      cancelled = true;
      cancelAnimationFrame(raf);
      if (retryTimer) clearTimeout(retryTimer);
      ro.disconnect();
    };
  }, [tickers]);

  const handleAddTicker = () => {
    if (newTicker && !tickers.includes(newTicker.toUpperCase())) {
      dispatch(addTicker(newTicker));
      dispatch(saveTickerTapePreferences([...tickers, newTicker.toUpperCase()]));
      setNewTicker('');
    }
  };

  const handleRemoveTicker = (ticker) => {
    dispatch(removeTicker(ticker));
    const updatedTickers = tickers.filter(t => t !== ticker);
    dispatch(saveTickerTapePreferences(updatedTickers));
  };

  const formatPrice = (price) => {
    return price ? price.toFixed(2) : '--';
  };

  const formatChange = (change, changePercent) => {
    const changeStr = change ? (change > 0 ? '+' : '') + change.toFixed(2) : '0.00';
    const percentStr = changePercent ? (changePercent > 0 ? '+' : '') + changePercent.toFixed(2) : '0.00';
    return `${changeStr} (${percentStr}%)`;
  };

  // For seamless infinite scroll, we need two identical copies side by side
  // The animation scrolls exactly the width of one copy, then instantly resets
  // This creates a seamless loop effect

  // Single DOM update pass: update all ticker prices at once via querySelectorAll
  // Runs on every rawPrices change but only touches DOM, no React re-render
  useEffect(() => {
    if (!contentRef.current) return;
    const els = contentRef.current.querySelectorAll('[data-ticker]');
    els.forEach(el => {
      const ticker = el.getAttribute('data-ticker');
      const pd = rawPrices[ticker];
      const priceEl = el.querySelector('.tape-price');
      const changeEl = el.querySelector('.tape-change');
      if (priceEl) {
        priceEl.textContent = pd?.price ? `$${pd.price.toFixed(2)}` : '--';
      }
      // What the price is (last / prev close / close) as a hover label.
      const kind = pd?.price ? priceBasis(pd.session, pd.rs) : null;
      if (kind) el.setAttribute('title', `${ticker} ${kind}`); else el.removeAttribute('title');
      if (changeEl && pd) {
        const ch = pd.change || 0;
        const pct = pd.changePercent || 0;
        const sign = ch > 0 ? '+' : '';
        changeEl.textContent = `${sign}${ch.toFixed(2)} (${sign}${pct.toFixed(2)}%)`;
        changeEl.style.color = ch > 0 ? '#4caf50' : ch < 0 ? '#f44336' : '#999';
      }
    });
  }, [rawPrices]);

  // Single copy of items (only recreate when ticker list changes — never on price updates)
  const singleCopyItems = useMemo(() => {
    return tickers.map((ticker) => (
      <TickerItem key={`single-${ticker}`} ticker={ticker} />
    ));
  }, [tickers]);

  // Apply scroll params as CSS custom properties on the content element.
  //
  // Changing --scroll-dist moves the animation's 100% keyframe, and changing
  // --scroll-dur re-maps elapsed time onto a new progress fraction. Doing
  // either to a running animation snaps the strip to a new position. So we read
  // where it currently sits, then restart the animation with a negative delay
  // that lands it on exactly the same pixel — the change becomes invisible.
  //
  // useLayoutEffect (not useEffect) so the properties are in place in the same
  // paint that switches `animation` on, instead of a frame spent animating
  // toward the -1000px/30s fallback.
  useLayoutEffect(() => {
    const el = contentRef.current;
    if (!el || scrollDistance <= 0) return;

    const prevDist = parseFloat(el.style.getPropertyValue('--scroll-dist')) || 0;
    const wasRunning = prevDist !== 0;

    // Where are we now, as a fraction of the loop?
    const phase = wasRunning
      ? Math.min(1, Math.max(0, Math.abs(currentTranslateX(el)) / Math.abs(prevDist)))
      : 0;

    el.style.setProperty('--scroll-dist', `-${scrollDistance}px`);
    el.style.setProperty('--scroll-dur', `${animationDuration}s`);

    if (wasRunning) {
      // Restart so the new delay takes effect, resuming at the same phase.
      el.style.animation = 'none';
      void el.offsetWidth; // force reflow to commit the cancellation
      el.style.animation = '';
      el.style.animationDelay = `-${(phase * animationDuration).toFixed(4)}s`;
    } else {
      el.style.animationDelay = '0s';
    }
  }, [scrollDistance, animationDuration]);

  return (
    <>
      {/* Static keyframes — injected once, never changes, never restarts */}
      <style>{`
        @keyframes tickerScroll {
          0% { transform: translate3d(0, 0, 0); }
          100% { transform: translate3d(var(--scroll-dist, -1000px), 0, 0); }
        }
      `}</style>
      <Paper
        elevation={1}
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 1100,
          backgroundColor: 'background.default',
          borderRadius: 0,
          overflow: 'hidden',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', height: 22 }}>
          <Box
            ref={containerRef}
            sx={{
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              overflow: 'hidden',
              whiteSpace: 'nowrap',
            }}
          >
            <Box
              ref={contentRef}
              sx={{
                display: 'inline-flex',
                animation: tickers.length > 0 && scrollDistance > 0
                  ? 'tickerScroll var(--scroll-dur, 30s) linear infinite'
                  : 'none',
                willChange: 'transform',
                backfaceVisibility: 'hidden',
                transform: 'translateZ(0)', // Force GPU compositing layer
                '&:hover': {
                  animationPlayState: 'paused',
                },
              }}
            >
              {/* First copy - used for measuring width */}
              <Box ref={singleCopyRef} sx={{ display: 'inline-flex' }}>
                {singleCopyItems}
              </Box>
              {/* Additional copies - creates seamless loop by filling viewport + scroll distance */}
              {Array.from({ length: numCopies - 1 }, (_, i) => (
                <Box key={`copy-${i}`} sx={{ display: 'inline-flex' }}>
                  {singleCopyItems}
                </Box>
              ))}
            </Box>
          </Box>
          <IconButton
            size="small"
            onClick={() => setSettingsOpen(true)}
            sx={{ mx: 0.5, p: 0.25 }}
          >
            <SettingsIcon sx={{ fontSize: '0.9rem' }} />
          </IconButton>
        </Box>
      </Paper>

      {/* Settings Dialog */}
      <Dialog
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        maxWidth="sm"
        fullWidth
        TransitionProps={{
          timeout: { enter: 150, exit: 0 },
          unmountOnExit: true
        }}
      >
        <DialogTitle>Manage Ticker Tape</DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 2 }}>
            <Autocomplete
              value={newTicker}
              onChange={(event, newValue) => setNewTicker(newValue || '')}
              inputValue={newTicker}
              onInputChange={(event, newInputValue) => setNewTicker(newInputValue)}
              options={(allTickers || []).filter(t => !tickers.includes(t))}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Add Ticker"
                  variant="outlined"
                  size="small"
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      handleAddTicker();
                    }
                  }}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {params.InputProps.endAdornment}
                        <IconButton
                          size="small"
                          onClick={handleAddTicker}
                          disabled={!newTicker || tickers.includes(newTicker.toUpperCase())}
                        >
                          <AddIcon />
                        </IconButton>
                      </>
                    ),
                  }}
                />
              )}
              freeSolo
            />
          </Box>

          <Typography variant="subtitle2" gutterBottom>
            Current Tickers ({tickers.length})
          </Typography>
          <List dense>
            {tickers.map((ticker) => (
              <ListItem key={ticker}>
                <ListItemText
                  primary={ticker}
                  secondary={
                    rawPrices[ticker]
                      ? `$${formatPrice(rawPrices[ticker].price)} ${formatChange(
                          rawPrices[ticker].change,
                          rawPrices[ticker].changePercent
                        )}`
                      : 'Loading...'
                  }
                />
                <ListItemSecondaryAction>
                  <IconButton
                    edge="end"
                    size="small"
                    onClick={() => handleRemoveTicker(ticker)}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </ListItemSecondaryAction>
              </ListItem>
            ))}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSettingsOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

// Memoize to prevent re-renders from parent state changes
export default memo(TickerTape);